import React, { useState, useEffect, useRef } from "react";
import { TextField, IconButton } from "@material-ui/core";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import moment from "moment";

const DdMmYyyyDateField = ({
  id,
  name,
  label,
  value,
  onChange,
  error,
  helperText,
  min,
  max,
  fullWidth = true,
  size = "small",
  variant = "outlined",
  disabled = false,
  required = false,
  className = "",
  style = {},
  InputLabelProps = {},
  InputProps = {},
}) => {
  const hiddenInputRef = useRef(null);

  const getDisplayValue = (val) => {
    if (!val) return "";
    const m = moment(val, ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY", "MM/DD/YYYY"], true);
    if (m.isValid()) {
      return m.format("DD/MM/YYYY");
    }
    const parsed = moment(val);
    if (parsed.isValid()) {
      return parsed.format("DD/MM/YYYY");
    }
    return String(val);
  };

  const getYmdValue = (val) => {
    if (!val) return "";
    const m = moment(val, ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY", "MM/DD/YYYY"], true);
    if (m.isValid()) {
      return m.format("YYYY-MM-DD");
    }
    const parsed = moment(val);
    if (parsed.isValid()) {
      return parsed.format("YYYY-MM-DD");
    }
    return "";
  };

  const [inputText, setInputText] = useState(() => getDisplayValue(value));

  useEffect(() => {
    setInputText(getDisplayValue(value));
  }, [value]);

  const handleTextChange = (e) => {
    let raw = e.target.value;
    let cleaned = raw.replace(/[^0-9/]/g, "");

    const digitsOnly = cleaned.replace(/\//g, "");
    if (!raw.endsWith("/") && digitsOnly.length <= 8) {
      if (digitsOnly.length >= 5) {
        cleaned = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4, 8)}`;
      } else if (digitsOnly.length >= 3) {
        cleaned = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}`;
      }
    }

    setInputText(cleaned);

    if (cleaned.length === 10) {
      const m = moment(cleaned, "DD/MM/YYYY", true);
      if (m.isValid() && typeof onChange === "function") {
        const syntheticEvent = {
          target: { name: name || id, value: m.format("YYYY-MM-DD") },
        };
        onChange(syntheticEvent);
      }
    } else if (cleaned === "" && typeof onChange === "function") {
      const syntheticEvent = {
        target: { name: name || id, value: "" },
      };
      onChange(syntheticEvent);
    }
  };

  const handleNativePickerChange = (e) => {
    const ymd = e.target.value;
    if (ymd) {
      const m = moment(ymd, "YYYY-MM-DD", true);
      if (m.isValid()) {
        setInputText(m.format("DD/MM/YYYY"));
        if (typeof onChange === "function") {
          const syntheticEvent = {
            target: { name: name || id, value: m.format("YYYY-MM-DD") },
          };
          onChange(syntheticEvent);
        }
      }
    }
  };

  const handleOpenPicker = (e) => {
    if (e) e.stopPropagation();
    if (hiddenInputRef.current) {
      if (typeof hiddenInputRef.current.showPicker === "function") {
        hiddenInputRef.current.showPicker();
      } else {
        hiddenInputRef.current.click();
      }
    }
  };

  return (
    <div style={{ position: "relative", width: fullWidth ? "100%" : "auto", ...style }}>
      <TextField
        fullWidth={fullWidth}
        id={id}
        name={name}
        label={label}
        variant={variant}
        size={size}
        disabled={disabled}
        required={required}
        className={className}
        value={inputText}
        placeholder="DD/MM/YYYY"
        onChange={handleTextChange}
        onClick={handleOpenPicker}
        error={error}
        helperText={helperText}
        InputLabelProps={{ shrink: true, style: { fontWeight: 400 }, ...InputLabelProps }}
        InputProps={{
          style: { fontWeight: 400, cursor: "pointer" },
          endAdornment: (
            <IconButton
              size="small"
              onClick={handleOpenPicker}
              style={{ padding: 4 }}
              tabIndex={-1}
              aria-label="Open calendar"
            >
              <CalendarTodayIcon fontSize="small" style={{ color: "#036dda" }} />
            </IconButton>
          ),
          ...InputProps,
        }}
      />
      <input
        ref={hiddenInputRef}
        type="date"
        value={getYmdValue(value)}
        min={getYmdValue(min)}
        max={getYmdValue(max)}
        onChange={handleNativePickerChange}
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "1px",
          height: "1px",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
        tabIndex={-1}
      />
    </div>
  );
};

export default DdMmYyyyDateField;
